import { CommonModule } from '@angular/common';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { ReactiveFormsModule, FormGroup, FormControl } from '@angular/forms';
import { PageEvent } from '@angular/material/paginator';
import { Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { MessageHandlerService } from '@core/services/message-handler/message-handler.service';
import { Store } from '@ngrx/store';
import { InputModule } from '@sharedModule/components/input/input.module';
import { sortDirection } from '@sharedModule/constants/data-grid.constants';
import { ATTRIBUTES_CONSTANT } from '@sharedModule/constants/fields.constant';
import { HasAccessDirective } from '@sharedModule/directives/has-access.directive';
import { MaterialModule } from '@sharedModule/material/material.module';
import { ConfirmationCommonDialogData } from '@sharedModule/models/confirmation-dialog.model';
import { ConfirmationDialogService } from '@sharedModule/services/confirmation-dialog/confirmation-dialog.service';
import { ExcelExportService } from '@sharedModule/services/excel-export/excel-export.service';
import { GlobalService } from '@sharedModule/services/global.service';
import { getActiveSort } from '@sharedModule/utils/sort.utils';
import { Subject, takeUntil } from 'rxjs';
import { CountrySettingsService } from '../../services/country-settings.service';
import {
  updateConversionRateFilter,
  deleteVelocityLimit,
} from '../../store/conversion-rate.actions';
import { selectConversionRate } from '../../store/conversion-rate.selector';
import { ConversionRateInterface } from '../../types/currency-conversion-rate';
import {
  defaultConversionRateFilter,
  ConversionRateFilter,
} from '../../types/currency-conversion-rate-filter';

@Component({
  selector: 'app-conversion-rate',
  standalone: true,
  imports: [
    InputModule,
    CommonModule,
    MaterialModule,
    HasAccessDirective,
    ReactiveFormsModule,
  ],
  templateUrl: './conversion-rate.component.html',
  styleUrl: './conversion-rate.component.scss',
})
export class ConversionRateComponent implements OnInit, OnDestroy {
  public readonly attrConstant =
    ATTRIBUTES_CONSTANT.settings.countrySettings.conversionRate;
  filterForm!: FormGroup;
  displayedColumns: string[] = [
    'countries',
    'transfer_type_po_name',
    'service_level_name',
    'payment_type_name',
    'other_fee',
    'markup_percentage',
    'master_rate',
    'action',
  ];
  conversionRates?: ConversionRateInterface[];
  dataSource!: MatTableDataSource<ConversionRateInterface>;
  activeSort?: Sort;
  fallBackSort = {
    active: 'created_at',
    direction: sortDirection.desc,
  };
  page = 0;
  total = 0;
  limit = 10;
  subject = new Subject<{
    search: string;
  }>();
  #destroy$ = new Subject<void>();

  constructor(
    private store: Store,
    private router: Router,
    private globalService: GlobalService,
    private excelExportService: ExcelExportService,
    private messageHandlerService: MessageHandlerService,
    private countrySettingsService: CountrySettingsService,
    private confirmationDialogService: ConfirmationDialogService
  ) {}

  ngOnInit(): void {
    this.filterForm = new FormGroup({
      [this.attrConstant.search.key]: new FormControl(''),
    });
    this.getConversionRate();
    this.dispatchFilter();
  }

  getConversionRate(): void {
    this.store
      .select(selectConversionRate)
      .pipe(takeUntil(this.#destroy$))
      .subscribe(data => {
        if (data) {
          this.conversionRates = data.rawData;
          this.dataSource = new MatTableDataSource(data.rawData);
          this.page = data.page;
          this.total = data.totalRecords;
          this.limit = data.count;
        }
      });
  }

  applyFilter(): void {
    this.subject.next(this.filterForm.value);
  }

  clearFilters(): void {
    this.filterForm.reset();
    this.dispatchFilter({ ...defaultConversionRateFilter });
  }

  reload(): void {
    this.dispatchFilter();
  }

  onPageChange(pageEvent: PageEvent): void {
    this.dispatchFilter({
      count: pageEvent.pageSize,
      page: pageEvent.pageIndex,
    });
  }

  onSortChange(sortEvent: Sort): void {
    this.activeSort = getActiveSort(sortEvent, this.fallBackSort);
    this.dispatchFilter({
      sort_key: this.activeSort.active,
      sort_by: this.activeSort.direction,
    });
  }

  dispatchFilter(conversionRateFilter?: ConversionRateFilter): void {
    this.store.dispatch(
      updateConversionRateFilter({ filter: conversionRateFilter ?? {} })
    );
  }

  export(): void {
    this.excelExportService.exportToExcel(
      this.conversionRates!,
      'conversionRates'
    );
  }

  onEdit(data: ConversionRateInterface): void {
    this.globalService.putDataToStream(data);
    this.router.navigate(['/settings/edit-conversion-rate']);
  }

  async onDeleteConversionRate(data: ConversionRateInterface): Promise<void> {
    const alertCommonDialogData: ConfirmationCommonDialogData = {
      title: '',
      message: `Are you sure you're going to delete this currency conversion setting?`,
      submitButtonText: 'Confirm',
    };
    const isConfirmed =
      await this.confirmationDialogService.openConfirmationDialog(
        alertCommonDialogData
      );
    if (isConfirmed) {
      this.countrySettingsService
        .deleteConversionRate({ id: data.id })
        .pipe(takeUntil(this.#destroy$))
        .subscribe({
          next: x => {
            if (x.success) {
              this.messageHandlerService.show(x.message);
              this.store.dispatch(
                deleteVelocityLimit({
                  id: data.id,
                })
              );
            }
          },
        });
    }
  }

  ngOnDestroy(): void {
    this.#destroy$.next();
    this.#destroy$.complete();
  }
}
